import React from 'react'
import { Link } from 'react-router-dom'
import SeoHead from '../components/SeoHead'
import { COMPANY, IMAGES } from '../data/siteData'
import { PAGE_META } from '../data/metaData'
import { getOptimizedImageUrl } from '../lib/imageHelper'
import ContactForm from '../components/ContactForm'

export default function ContactPage() {
  const metaTitle = PAGE_META.contact.title
  const metaDescription = PAGE_META.contact.description

  return (
    <div>
      <SeoHead
        title={metaTitle}
        description={metaDescription}
        canonicalPath="/kontakt"
      />

      {/* Header */}
      <section className="relative bg-stone-950 text-white py-16 sm:py-20 overflow-hidden border-b border-stone-800">
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl(IMAGES.hero, { width: 1800, quality: 80 })}
            alt="JT Knudsen - haveanlæg og udgravning med minigraver"
            className="w-full h-full object-cover opacity-60 brightness-95"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/80 via-stone-950/50 to-stone-950/20"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold uppercase tracking-wider mb-3">
              <i className="fa-solid fa-phone-volume" aria-hidden="true"></i>
              <span>Kontakt & Tilbud</span>
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight drop-shadow-md">
              Kontakt Jonas Knudsen
            </h1>
            <p className="mt-4 text-base sm:text-xl text-stone-200 leading-relaxed drop-shadow-sm">
              Få et hurtigt og uforpligtende prisoverslag på din opgave. Uanset om det gælder nedgravning af trampolin, belægning, fundament eller maskingravning, er du velkommen til at ringe eller skrive.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 sm:py-24 bg-stone-50 text-stone-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Col: Contact Information */}
            <div className="lg:col-span-5 space-y-6">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-amber-600">
                  Virksomhedsoplysninger
                </span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight mt-1">
                  Direkte kontakt til din lokale fagmand
                </h2>
                <p className="mt-3 text-stone-600 text-sm sm:text-base leading-relaxed">
                  Hos <Link to="/" className="text-stone-900 font-semibold underline decoration-amber-500 decoration-2 underline-offset-2 hover:text-amber-600 transition-colors">JT Knudsen</Link> er der ingen lange ventetider. Du taler direkte med den person, som kender maskinerne og løser arbejdet på din grund.
                </p>
              </div>

              {/* Information cards */}
              <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-7 shadow-sm space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-amber-500/15 text-amber-600 flex items-center justify-center shrink-0 text-lg">
                    <i className="fa-solid fa-building" aria-hidden="true"></i>
                  </div>
                  <div>
                    <span className="text-xs text-stone-500 font-semibold uppercase tracking-wider block">
                      Virksomhed
                    </span>
                    <span className="text-base font-bold text-stone-900 block mt-0.5">
                      {COMPANY.name}
                    </span>
                    <span className="text-xs text-stone-600">
                      Indehaver: {COMPANY.owner}
                    </span>
                  </div>
                </div>

                <div className="flex items-start gap-4 pt-4 border-t border-stone-100">
                  <div className="w-11 h-11 rounded-xl bg-amber-500/15 text-amber-600 flex items-center justify-center shrink-0 text-lg">
                    <i className="fa-solid fa-phone" aria-hidden="true"></i>
                  </div>
                  <div>
                    <span className="text-xs text-stone-500 font-semibold uppercase tracking-wider block">
                      Telefon (klikbar)
                    </span>
                    <a
                      href={COMPANY.phoneHref}
                      className="text-lg font-bold text-stone-900 hover:text-amber-600 transition-colors whitespace-nowrap block mt-0.5"
                    >
                      {COMPANY.phone}
                    </a>
                    <span className="text-xs text-stone-500">
                      Ring gerne alle hverdage
                    </span>
                  </div>
                </div>

                <div className="flex items-start gap-4 pt-4 border-t border-stone-100">
                  <div className="w-11 h-11 rounded-xl bg-amber-500/15 text-amber-600 flex items-center justify-center shrink-0 text-lg">
                    <i className="fa-solid fa-envelope" aria-hidden="true"></i>
                  </div>
                  <div>
                    <span className="text-xs text-stone-500 font-semibold uppercase tracking-wider block">
                      E-mail
                    </span>
                    <a
                      href={`mailto:${COMPANY.email}`}
                      className="text-base font-bold text-stone-900 hover:text-amber-600 transition-colors whitespace-nowrap break-keep block mt-0.5"
                    >
                      {COMPANY.email}
                    </a>
                    <span className="text-xs text-stone-500">
                      Svarer inden for 24 timer
                    </span>
                  </div>
                </div>

                <div className="flex items-start gap-4 pt-4 border-t border-stone-100">
                  <div className="w-11 h-11 rounded-xl bg-amber-500/15 text-amber-600 flex items-center justify-center shrink-0 text-lg">
                    <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
                  </div>
                  <div>
                    <span className="text-xs text-stone-500 font-semibold uppercase tracking-wider block">
                      Adresse
                    </span>
                    <span className="text-base font-semibold text-stone-800 block mt-0.5">
                      {COMPANY.address}
                    </span>
                  </div>
                </div>
              </div>

              {/* Service Areas */}
              <div className="bg-stone-100 p-6 rounded-2xl border border-stone-200">
                <h3 className="font-bold text-stone-900 text-sm uppercase tracking-wider mb-2">
                  Dækningsområde
                </h3>
                <p className="text-xs sm:text-sm text-stone-600 mb-3">
                  Vi kører ud fra Hjortshøj til opgaver i hele Aarhus og omegn, Østjylland og Djursland:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {COMPANY.serviceAreas.map((area) => (
                    <span
                      key={area}
                      className="px-2.5 py-1 rounded bg-white border border-stone-300 text-stone-800 text-xs font-medium"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Col: Contact Form */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
